import React from 'react';
import { Link } from 'react-router-dom';
import { useDashboard, useInvalidate } from '../hooks/useApi';
import api from '../services/api';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  TicketIcon,
  UserPlusIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

export default function Dashboard() {
  const { data, isLoading, error } = useDashboard();
  const { invalidateDashboard } = useInvalidate();

  const handleCheckin = async (ticketId) => {
    try {
      await api.post(`/tickets/${ticketId}/checkin`);
      toast.success('Check-in u krye!');
      invalidateDashboard();
    } catch (error) {
      toast.error('Gabim');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500">Gabim gjatë ngarkimit të dashboard</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header me Aksione */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mirë se vini!</h1>
          <p className="text-gray-500">{dayjs().format('dddd, D MMMM YYYY')}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/tickets/new" className="btn-primary flex items-center gap-2">
            <PlusIcon className="w-5 h-5" />
            Shto Biletë
          </Link>
          <Link to="/clients" className="btn-secondary flex items-center gap-2">
            <UserPlusIcon className="w-5 h-5" />
            Klientët
          </Link>
          <Link to="/payments" className="btn-secondary flex items-center gap-2">
            <BanknotesIcon className="w-5 h-5" />
            Pagesat
          </Link>
        </div>
      </div>

      {/* Statistika Ditore */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Bileta Sot</p>
              <p className="text-3xl font-bold text-gray-900">{data?.today?.tickets_count || 0}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <TicketIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Shitje Sot</p>
              <p className="text-3xl font-bold text-green-600">€{(data?.today?.sales || 0).toFixed(0)}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <BanknotesIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Bileta Muaji</p>
              <p className="text-3xl font-bold text-gray-900">{data?.month?.tickets_count || 0}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <PaperAirplaneIcon className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Shitje Muaji</p>
              <p className="text-3xl font-bold text-green-600">€{(data?.month?.sales || 0).toFixed(0)}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <BanknotesIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Check-in Alert */}
      {data?.checkin_tomorrow?.length > 0 && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-5 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-white/20 rounded-lg">
              <CheckCircleIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Check-in për Nesër</h2>
              <p className="text-amber-100 text-sm">{data.checkin_tomorrow.length} pasagjerë presin check-in</p>
            </div>
          </div>
          <div className="space-y-2">
            {data.checkin_tomorrow.slice(0, 5).map((ticket) => (
              <div key={ticket.id} className="flex items-center justify-between bg-white/10 rounded-lg p-3">
                <div>
                  <p className="font-medium">{ticket.passenger_name}</p>
                  <p className="text-amber-100 text-sm">{ticket.ticket_code} → {ticket.destination?.name}</p>
                </div>
                <button
                  onClick={() => handleCheckin(ticket.id)}
                  className="px-4 py-2 bg-white text-amber-600 rounded-lg font-medium hover:bg-amber-50 transition-colors"
                >
                  Check-in ✓
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Biletat e Fundit */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Biletat e Fundit</h2>
            <Link to="/tickets" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
              Të gjitha <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-gray-50">
            {data?.recent_tickets?.length > 0 ? (
              data.recent_tickets.slice(0, 6).map((ticket) => (
                <div key={ticket.id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center">
                      <PaperAirplaneIcon className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{ticket.passenger_name}</p>
                      <p className="text-sm text-gray-500">
                        {ticket.destination?.name} • {dayjs(ticket.ticket_date).format('DD/MM/YY')}
                      </p>
                    </div>
                  </div>
                  <p className="font-semibold text-gray-900">€{parseFloat(ticket.total).toFixed(0)}</p>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <TicketIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Nuk ka bileta ende</p>
                <Link to="/tickets/new" className="text-primary-600 hover:underline text-sm">
                  Shto biletën e parë
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Aksione të Shpejta */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Aksione të Shpejta</h2>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/tickets/new" className="flex flex-col items-center justify-center p-5 bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all shadow-sm">
              <TicketIcon className="w-8 h-8 mb-2" />
              <span className="font-medium">Biletë e Re</span>
            </Link>
            <Link to="/clients" className="flex flex-col items-center justify-center p-5 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm">
              <UserPlusIcon className="w-8 h-8 mb-2" />
              <span className="font-medium">Klientët</span>
            </Link>
            <Link to="/payments" className="flex flex-col items-center justify-center p-5 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-sm">
              <BanknotesIcon className="w-8 h-8 mb-2" />
              <span className="font-medium">Pagesat</span>
            </Link>
            <Link to="/reports" className="flex flex-col items-center justify-center p-5 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all shadow-sm">
              <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="font-medium">Raporte</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
